import * as React from "react";
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { 
  Feedback as FeedbackIcon,
  Send as SendIcon 
} from "@mui/icons-material";
import { useState } from "react";

export default function ModernFeedback() {
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleIframeLoad = () => {
    setLoading(false);
  };

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      backgroundColor: "#fafafa",
      py: { xs: 4, md: 6 }
    }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            gap: 2, 
            mb: 3 
          }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "16px",
                backgroundColor: "rgba(46, 46, 46, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid rgba(46, 46, 46, 0.2)"
              }}
            >
              <FeedbackIcon sx={{ fontSize: 30, color: "#2e2e2e" }} />
            </Box>
          </Box>
          
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontSize: { xs: "2rem", md: "2.75rem" },
              fontWeight: 700,
              color: "#2e2e2e",
              mb: 2,
              background: "linear-gradient(135deg, #2e2e2e 0%, #546e7a 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Your Feedback Matters
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#666",
              fontWeight: 400,
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: 1.6,
              mb: 3
            }}
          >
            Help us improve ILASP procurement processes by sharing your thoughts, 
            suggestions, and experiences with our platform.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Chip 
              icon={<SendIcon />}
              label="Quick & Easy" 
              sx={{ 
                backgroundColor: "rgba(46, 46, 46, 0.1)",
                color: "#2e2e2e",
                fontWeight: 600,
                fontSize: "0.875rem",
                padding: "4px 8px"
              }} 
            />
            <Chip 
              label="Anonymous Option Available" 
              sx={{ 
                backgroundColor: "rgba(46, 46, 46, 0.1)",
                color: "#2e2e2e",
                fontWeight: 600,
                fontSize: "0.875rem",
                padding: "4px 8px"
              }} 
            />
            <Chip 
              label="Direct to Development Team" 
              sx={{ 
                backgroundColor: "rgba(46, 46, 46, 0.1)",
                color: "#2e2e2e",
                fontWeight: 600,
                fontSize: "0.875rem",
                padding: "4px 8px"
              }} 
            />
          </Box>
        </Box>

        {/* Form Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "24px",
            overflow: "hidden",
            backgroundColor: "#fff",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(46, 46, 46, 0.05)",
            border: "1px solid rgba(46, 46, 46, 0.1)",
            position: "relative",
            minHeight: "600px"
          }}
        >
          {/* Loading Overlay */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fff",
                zIndex: 2,
                gap: 3
              }}
            >
              <CircularProgress 
                sx={{ color: "#2e2e2e" }} 
                size={50} 
              />
              <Typography 
                variant="body1" 
                sx={{ 
                  color: "#666",
                  fontWeight: 500
                }}
              >
                Loading feedback form...
              </Typography>
            </Box>
          )}

          {/* Modern iframe wrapper */}
          <Box
            sx={{
              p: { xs: 0, md: 3 },
              background: "linear-gradient(145deg, #fafafa 0%, #fff 100%)",
              borderRadius: "24px",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #2e2e2e 0%, #546e7a 50%, #2e2e2e 100%)",
                borderRadius: "24px 24px 0 0"
              }
            }}
          >
            <iframe
              src="https://docs.google.com/forms/d/e/1FAIpQLSdiTGqoFJhnPaPzJgJ5XCNe_aKHhF98XUGZjN1IdYZwNmWrmA/viewform?embedded=true"
              width="100%"
              height={isMobile ? "1800" : "2200"}
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="ILASP Procurement Feedback Form"
              onLoad={handleIframeLoad}
              style={{ 
                borderRadius: isMobile ? "0" : "20px",
                backgroundColor: "#fff",
                border: "none"
              }}
            >
              Loading feedback form...
            </iframe>
          </Box>
        </Paper>

        {/* Footer Info */}
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: "#666",
              lineHeight: 1.6,
              maxWidth: "600px",
              margin: "0 auto"
            }}
          >
            Your feedback is valuable to us and helps improve the ILASP procurement experience. 
            All submissions are reviewed by our development team and may influence future updates.
          </Typography>
          
          <Box 
            sx={{ 
              mt: 3,
              pt: 3,
              borderTop: "1px solid rgba(46, 46, 46, 0.1)"
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                color: "#999",
                fontSize: "0.8rem"
              }}
            >
              This form is powered by Google Forms and follows our privacy policy
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}